/**
 * Year6PocketBoothPage — /year6/:slug/booth
 *
 * "Pocket Booth" — phone-based, kid-friendly, ZERO friction.
 * Tap → capture → upload. No filters, no editing, no countdown gymnastics.
 *
 * Year 6 themed overlays (a small starter pack):
 *   • Polaroid white frame   • "Class of {year}" banner    • Mortarboard cap glyph
 *   • Confetti               • School-name eyebrow
 *
 * The overlay is selected before capture, then composited onto the captured
 * frame at full resolution. Photos are tagged source=pocket_booth so the
 * Memory Book PDF can render them in a dedicated polaroid spread.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Camera, VideoCamera, ArrowsClockwise, X, Check, Image as ImageIcon,
  GraduationCap, ArrowLeft,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Overlay catalog (kept tiny; "Tap → capture → upload" stays simple) ──
const OVERLAYS = [
  { id: 'polaroid',    label: 'Polaroid',    glyph: '🖼️' },
  { id: 'graduation',  label: 'Graduation',  glyph: '🎓' },
  { id: 'class_of',    label: 'Class of',    glyph: '🎉' },
  { id: 'confetti',    label: 'Confetti',    glyph: '✨' },
  { id: 'plain',       label: 'No overlay',  glyph: '⊘' },
];

const CAPTURE_W = 1080;            // square portrait, tuned for phones
const CAPTURE_H = 1080;
const VIDEO_MAX_SECONDS = 8;       // short, fun, on-brand. Still uploadable.

export default function Year6PocketBoothPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [stage, setStage] = useState('live');     // live | preview | uploading | saved
  const [mode, setMode] = useState('photo');      // photo | video
  const [overlayId, setOverlayId] = useState('graduation');
  const [facing, setFacing] = useState('user');   // 'user' | 'environment'
  const [streamReady, setStreamReady] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordSec, setRecordSec] = useState(0);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordTimerRef = useRef(null);

  // ── Load event ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        setEvent(r.data);
        if (r.data?.event_date) setYear(String(r.data.event_date).slice(0, 4));
      } catch {
        if (!cancelled) toast.error('Could not load this class page');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // ── Camera lifecycle ──
  const startCamera = useCallback(async () => {
    setStreamError(null);
    try {
      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: 1280 },
          height: { ideal: 1280 },
        },
        audio: mode === 'video',
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => { /* iOS autoplay needs user gesture; we'll retry on first tap */ });
      }
      setStreamReady(true);
    } catch (e) {
      setStreamError(
        e?.name === 'NotAllowedError'
          ? 'Camera permission denied. Tap the address bar lock icon → Allow camera, then refresh.'
          : 'Could not access the camera on this device.'
      );
    }
  }, [facing, mode]);

  useEffect(() => {
    if (stage === 'live') startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing, mode]);

  // When returning to `live` from `saved` / `preview`, the <video> element
  // re-mounts so its srcObject is empty. Re-bind to the live stream so we
  // don't have to re-acquire the camera (faster, no permission re-prompt).
  useEffect(() => {
    if (stage === 'live' && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => { /* user gesture may be needed */ });
    }
  }, [stage]);

  // ── Compositing helpers ──
  // Draws the chosen overlay over a canvas context the size of (w, h).
  const drawOverlay = (ctx, w, h, id, schoolName, yr) => {
    if (!id || id === 'plain') return;
    const f = w / 1080;  // scale factor

    if (id === 'polaroid') {
      const padX = 38 * f, padTop = 38 * f, padBottom = 110 * f;
      ctx.save();
      ctx.fillStyle = 'rgba(255,255,255,0.96)';
      ctx.fillRect(0, 0, w, padTop);
      ctx.fillRect(0, h - padBottom, w, padBottom);
      ctx.fillRect(0, 0, padX, h);
      ctx.fillRect(w - padX, 0, padX, h);
      ctx.fillStyle = '#0F1014';
      ctx.font = `${28 * f}px Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`${schoolName || 'Year 6'} · Class of ${yr}`, w / 2, h - 50 * f);
      ctx.restore();
      return;
    }

    if (id === 'graduation') {
      // Top-bar with mortarboard glyph + label
      ctx.save();
      ctx.fillStyle = 'rgba(10,11,26,0.55)';
      ctx.fillRect(0, 0, w, 130 * f);
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${56 * f}px -apple-system, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'left';
      ctx.fillText('🎓', 36 * f, 92 * f);
      ctx.font = `bold ${32 * f}px Georgia, serif`;
      ctx.fillText(`${schoolName || 'Year 6'} — Leavers ${yr}`, 116 * f, 84 * f);
      ctx.restore();
      return;
    }

    if (id === 'class_of') {
      // Bottom banner with serif "Class of YYYY"
      ctx.save();
      const grad = ctx.createLinearGradient(0, h - 200 * f, 0, h);
      grad.addColorStop(0, 'rgba(10,11,26,0)');
      grad.addColorStop(1, 'rgba(10,11,26,0.85)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, h - 200 * f, w, 200 * f);
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.font = `italic ${72 * f}px Georgia, serif`;
      ctx.fillText(`Class of ${yr}`, w / 2, h - 88 * f);
      ctx.font = `${22 * f}px -apple-system, "Segoe UI", Roboto, sans-serif`;
      ctx.fillStyle = 'rgba(255,255,255,0.75)';
      ctx.fillText(schoolName || 'Year 6 Leavers', w / 2, h - 50 * f);
      ctx.restore();
      return;
    }

    if (id === 'confetti') {
      // Procedurally sprinkle dots — deterministic per session for consistency.
      ctx.save();
      const colors = ['#FACC15', '#EC4899', '#8B5CF6', '#22D3EE', '#22C55E'];
      for (let i = 0; i < 60; i++) {
        const px = (Math.sin(i * 12.9898) * 43758.5453) % 1;
        const py = (Math.sin(i * 78.233) * 43758.5453) % 1;
        const x = Math.abs(px) * w;
        const y = Math.abs(py) * h * 0.35; // confetti only top third
        ctx.fillStyle = colors[i % colors.length];
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        ctx.arc(x, y, 6 * f + (i % 4) * f, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${28 * f}px Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`${schoolName || 'Year 6'} · Class of ${yr}`, w / 2, h - 36 * f);
      ctx.restore();
      return;
    }
  };

  // ── Capture → composited blob ──
  const capturePhoto = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (!video.videoWidth) {
      toast.error('Camera not ready yet');
      return;
    }
    canvas.width = CAPTURE_W;
    canvas.height = CAPTURE_H;
    const ctx = canvas.getContext('2d');

    // Cover-fit + horizontal flip when using front camera (selfies feel natural mirrored)
    const vw = video.videoWidth, vh = video.videoHeight;
    const scale = Math.max(CAPTURE_W / vw, CAPTURE_H / vh);
    const dw = vw * scale, dh = vh * scale;
    const dx = (CAPTURE_W - dw) / 2, dy = (CAPTURE_H - dh) / 2;
    ctx.save();
    if (facing === 'user') { ctx.translate(CAPTURE_W, 0); ctx.scale(-1, 1); }
    ctx.drawImage(video, dx, dy, dw, dh);
    ctx.restore();

    drawOverlay(ctx, CAPTURE_W, CAPTURE_H, overlayId,
                event?.school_name || event?.event_name, year);

    canvas.toBlob((blob) => {
      if (!blob) { toast.error('Capture failed'); return; }
      setPreviewBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setStage('preview');
    }, 'image/jpeg', 0.92);
  }, [overlayId, facing, event, year]);

  // ── Video record ──
  const startVideo = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    try {
      const mr = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp8,opus',
      });
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setPreviewBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        setStage('preview');
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
      setRecordSec(0);
      recordTimerRef.current = setInterval(() => {
        setRecordSec((s) => {
          if (s + 1 >= VIDEO_MAX_SECONDS) {
            stopVideo();
            return VIDEO_MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error('Video recording not supported on this device');
    }
  }, []);

  const stopVideo = useCallback(() => {
    if (recordTimerRef.current) { clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    setRecording(false);
  }, []);

  // ── Upload ──
  const upload = useCallback(async () => {
    if (!previewBlob || !event?.event_id) return;
    setStage('uploading');
    const isVideo = mode === 'video';
    const ext = isVideo ? 'webm' : 'jpg';
    const file = new File([previewBlob], `pocket-booth-${Date.now()}.${ext}`, {
      type: previewBlob.type || (isVideo ? 'video/webm' : 'image/jpeg'),
    });
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(
        `${API}/events/${event.event_id}/photos?source=pocket_booth`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(isVideo ? 'Video uploaded ✨' : 'Photo uploaded ✨');
      // Drop the preview and surface a "🎉 Saved! / Take another →" screen so
      // kids tap once and immediately fire another capture. This is the
      // single biggest driver of repeat captures (volume → Memory Book quality).
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewBlob(null);
      setPreviewUrl(null);
      setStage('saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed — try again');
      setStage('preview');
    }
  }, [previewBlob, previewUrl, event, mode]);

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewBlob(null);
    setPreviewUrl(null);
    setStage('live');
  };

  // ── Render ──
  if (!event) {
    return (
      <div className="min-h-screen bg-[#0F1014] flex items-center justify-center" data-testid="year6-booth-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/40 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1014] text-white flex flex-col" data-testid="year6-pocket-booth">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/8">
        <button
          onClick={() => navigate(`/e/${slug}`)}
          className="text-white/65 hover:text-white text-xs font-bold inline-flex items-center gap-1.5"
          data-testid="year6-booth-back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </button>
        <div className="text-center">
          <p className="text-[9.5px] font-black uppercase tracking-[0.18em] text-[#C29D52] mb-0.5">Pocket Booth</p>
          <h1 className="text-[15px] font-black inline-flex items-center gap-1.5" style={{ fontFamily: 'Georgia, serif' }}>
            <GraduationCap size={15} weight="fill" className="text-amber-300" />
            {event.school_name || 'Year 6'}
          </h1>
        </div>
        <button
          onClick={() => setFacing(f => f === 'user' ? 'environment' : 'user')}
          aria-label="Switch camera"
          className="text-white/65 hover:text-white p-1.5"
          data-testid="year6-booth-flip-btn"
        >
          <ArrowsClockwise size={16} weight="bold" />
        </button>
      </div>

      {/* Mode tabs */}
      <div className="px-4 pt-3">
        <div className="inline-flex rounded-full p-1 bg-white/[0.06] border border-white/10" role="tablist">
          {[
            { id: 'photo', label: 'Photo', icon: Camera },
            { id: 'video', label: 'Video', icon: VideoCamera },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={mode === id}
              onClick={() => { setMode(id); }}
              disabled={stage !== 'live' || recording}
              className={`px-4 py-1.5 rounded-full text-[12px] font-black inline-flex items-center gap-1.5 transition-colors ${
                mode === id ? 'bg-white text-[#0F1014]' : 'text-white/65'
              }`}
              data-testid={`year6-booth-mode-${id}`}
            >
              <Icon size={12} weight="bold" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Camera viewport */}
      <div className="relative flex-1 mx-4 my-3 rounded-2xl overflow-hidden bg-black aspect-square max-h-[70vh]">
        {stage === 'saved' ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
            style={{ background: 'radial-gradient(circle at 50% 35%, rgba(236,72,153,0.18), rgba(10,11,26,0.95) 70%)' }}
            data-testid="year6-booth-saved-view"
          >
            <div className="text-6xl mb-3 animate-[bounce_1.2s_ease-out_1]" aria-hidden="true">🎉</div>
            <h2 className="text-white text-2xl font-black mb-1" style={{ fontFamily: 'Georgia, serif' }}>
              Saved!
            </h2>
            <p className="text-white/65 text-sm">Tap below to take another</p>
          </div>
        ) : stage === 'preview' && previewUrl ? (
          mode === 'video' ? (
            <video
              src={previewUrl}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="year6-booth-preview-video"
            />
          ) : (
            <img
              src={previewUrl}
              alt="Capture preview"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="year6-booth-preview-img"
            />
          )
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`absolute inset-0 w-full h-full object-cover ${facing === 'user' ? 'scale-x-[-1]' : ''}`}
              data-testid="year6-booth-live-video"
            />
            {/* Live overlay preview (HTML — not the captured frame; capture composites again on canvas) */}
            <BoothOverlayPreview
              overlayId={overlayId}
              schoolName={event.school_name || event.event_name}
              year={year}
            />
            {streamError && (
              <div className="absolute inset-0 flex items-center justify-center text-center px-6 bg-black/85" data-testid="year6-booth-stream-error">
                <p className="text-white/85 text-sm font-bold max-w-xs">{streamError}</p>
              </div>
            )}
            {!streamReady && !streamError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-300 border-t-transparent" />
              </div>
            )}
            {recording && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-rose-500 text-white text-[11px] font-black flex items-center gap-1.5" data-testid="year6-booth-recording">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                REC · 0:0{Math.max(0, VIDEO_MAX_SECONDS - recordSec)}
              </div>
            )}
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Overlay picker */}
      {stage === 'live' && (
        <div className="px-4 pb-3 overflow-x-auto" data-testid="year6-booth-overlay-picker">
          <div className="flex gap-2 min-w-max">
            {OVERLAYS.map((ov) => (
              <button
                key={ov.id}
                onClick={() => setOverlayId(ov.id)}
                className={`shrink-0 px-3 py-2 rounded-xl text-[12px] font-bold border inline-flex items-center gap-1.5 transition-colors ${
                  overlayId === ov.id
                    ? 'bg-amber-300 text-black border-amber-300'
                    : 'bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]'
                }`}
                data-testid={`year6-booth-overlay-${ov.id}`}
              >
                <span aria-hidden="true">{ov.glyph}</span>
                {ov.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="px-4 pb-6 pt-2 border-t border-white/8 bg-[#0F1014]">
        {stage === 'live' && (
          <div className="flex items-center justify-center">
            {mode === 'photo' ? (
              <button
                onClick={capturePhoto}
                disabled={!streamReady}
                aria-label="Take photo"
                className="w-20 h-20 rounded-full border-4 border-white/30 bg-white shadow-2xl active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="year6-booth-capture-btn"
              />
            ) : recording ? (
              <button
                onClick={stopVideo}
                aria-label="Stop recording"
                className="w-20 h-20 rounded-full border-4 border-white/30 bg-rose-500 shadow-2xl active:scale-95 inline-flex items-center justify-center"
                data-testid="year6-booth-stop-btn"
              >
                <span className="w-6 h-6 rounded-sm bg-white" />
              </button>
            ) : (
              <button
                onClick={startVideo}
                disabled={!streamReady}
                aria-label="Start video"
                className="w-20 h-20 rounded-full border-4 border-white/30 bg-rose-500 shadow-2xl active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="year6-booth-record-btn"
              />
            )}
          </div>
        )}
        {stage === 'preview' && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={retake}
              className="py-3 rounded-xl border border-white/15 bg-white/[0.04] text-white text-sm font-black inline-flex items-center justify-center gap-2"
              data-testid="year6-booth-retake-btn"
            >
              <X size={14} weight="bold" /> Retake
            </button>
            <button
              onClick={upload}
              className="py-3 rounded-xl text-sm font-black text-black inline-flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #FACC15 100%)' }}
              data-testid="year6-booth-save-btn"
            >
              <Check size={14} weight="bold" /> Save
            </button>
          </div>
        )}
        {stage === 'uploading' && (
          <div className="text-center py-3 inline-flex items-center justify-center gap-2 text-white/75 w-full">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-300 border-t-transparent" />
            <span className="text-sm font-bold">Uploading…</span>
          </div>
        )}
        {stage === 'saved' && (
          <button
            onClick={() => setStage('live')}
            className="w-full py-4 rounded-2xl text-base font-black text-black shadow-2xl active:scale-[0.99] transition-transform"
            style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #FACC15 100%)' }}
            data-testid="year6-booth-take-another-btn"
          >
            Take another →
          </button>
        )}
        <p className="text-center text-[11px] text-white/35 mt-3">
          <ImageIcon size={11} weight="bold" className="inline mb-0.5 mr-1" />
          Photos save straight to your Memory Book
        </p>
      </div>
    </div>
  );
}


// ─── Live overlay preview (HTML — visual hint only; capture re-paints on canvas)
function BoothOverlayPreview({ overlayId, schoolName, year }) {
  if (!overlayId || overlayId === 'plain') return null;

  if (overlayId === 'polaroid') {
    return (
      <>
        <div className="absolute inset-x-0 top-0 h-[6%] bg-white/95" />
        <div className="absolute inset-x-0 bottom-0 h-[14%] bg-white/95 flex items-center justify-center">
          <p className="text-[#0F1014]" style={{ fontFamily: 'Georgia, serif', fontSize: '3.2vw' }}>
            {schoolName || 'Year 6'} · Class of {year}
          </p>
        </div>
        <div className="absolute inset-y-0 left-0 w-[4%] bg-white/95" />
        <div className="absolute inset-y-0 right-0 w-[4%] bg-white/95" />
      </>
    );
  }
  if (overlayId === 'graduation') {
    return (
      <div className="absolute inset-x-0 top-0 px-4 py-3 bg-gradient-to-b from-black/55 to-transparent flex items-center gap-2">
        <span className="text-3xl" aria-hidden="true">🎓</span>
        <p className="text-white font-black text-base" style={{ fontFamily: 'Georgia, serif' }}>
          {schoolName || 'Year 6'} — Leavers {year}
        </p>
      </div>
    );
  }
  if (overlayId === 'class_of') {
    return (
      <div className="absolute inset-x-0 bottom-0 pt-12 pb-5 bg-gradient-to-t from-black/85 to-transparent text-center">
        <p className="text-white" style={{ fontFamily: 'Georgia, serif', fontSize: '7vw', fontStyle: 'italic' }}>
          Class of {year}
        </p>
        <p className="text-white/75 text-[11px] mt-0.5">{schoolName || 'Year 6 Leavers'}</p>
      </div>
    );
  }
  if (overlayId === 'confetti') {
    const dots = Array.from({ length: 24 });
    return (
      <>
        {dots.map((_, i) => {
          const x = Math.abs((Math.sin(i * 12.9898) * 43758.5453) % 1) * 100;
          const y = Math.abs((Math.sin(i * 78.233) * 43758.5453) % 1) * 30;
          const colors = ['#FACC15', '#EC4899', '#8B5CF6', '#22D3EE', '#22C55E'];
          return (
            <div key={i} className="absolute rounded-full"
              style={{
                left: `${x}%`, top: `${y}%`,
                width: 6 + (i % 4) * 2, height: 6 + (i % 4) * 2,
                background: colors[i % colors.length], opacity: 0.85,
              }} />
          );
        })}
        <div className="absolute inset-x-0 bottom-3 text-center">
          <p className="text-white font-black" style={{ fontFamily: 'Georgia, serif', fontSize: '3.5vw' }}>
            {schoolName || 'Year 6'} · Class of {year}
          </p>
        </div>
      </>
    );
  }
  return null;
}
